import { database } from "../lib/firebase";
import { ref, set, get, onValue, update } from "firebase/database";

var DEFAULT_SCREEN_CONFIG = {
  show_date: true,
  time_format: "24h",
  date_format: "DD/MM/YYYY",
  clock_style: "modern",
  background_color: "#0A0A0A",
  text_color: "#FFFFFF",
  clock_size: "large",
  font_family: "Anton",
  logo_url: null,
  logo_position: "top-left",
  logo_size: "medium",
  message_enabled: false,
  message_text: "",
  message_animation: "static",
  message_mode: "manual",
  message_duration: 30,
  effect_enabled: true,
  effect_style: "glow_pulse"
};

var SCREENS_STORAGE_KEY = "mvplab_screens_data";

function getLocalScreens() {
  try {
    var data = localStorage.getItem(SCREENS_STORAGE_KEY);
    if (data) {
      var parsed = JSON.parse(data);
      if (Array.isArray(parsed) && parsed.length === 6) {
        return parsed;
      }
    }
  } catch (e) {}
  return null;
}

function saveLocalScreens(screens) {
  try {
    localStorage.setItem(SCREENS_STORAGE_KEY, JSON.stringify(screens));
  } catch (e) {}
}

function buildDefaultScreens() {
  var screens = [];
  for (var i = 1; i <= 6; i++) {
    screens.push(Object.assign({}, DEFAULT_SCREEN_CONFIG, {
      screen_number: i,
      custom_name: "Screen " + i,
      updated_at: new Date().toISOString()
    }));
  }
  return screens;
}

function screensArrayToObject(screens) {
  var obj = {};
  for (var i = 0; i < screens.length; i++) {
    obj["screen_" + screens[i].screen_number] = screens[i];
  }
  return obj;
}

function screensObjectToArray(obj) {
  var arr = [];
  for (var i = 1; i <= 6; i++) {
    var key = "screen_" + i;
    if (obj[key]) {
      arr.push(obj[key]);
    }
  }
  return arr;
}

async function initFirebase() {
  if (!database) {
    var localScreens = getLocalScreens();
    if (localScreens) return localScreens;
    var screens = buildDefaultScreens();
    saveLocalScreens(screens);
    return screens;
  }
  var screensRef = ref(database, "screens");
  try {
    var snapshot = await get(screensRef);
    if (snapshot.exists()) {
      var data = snapshot.val();
      var screens = screensObjectToArray(data);
      if (screens.length === 6) {
        saveLocalScreens(screens);
        return screens;
      }
    }
    var localScreens = getLocalScreens();
    var screens = localScreens || buildDefaultScreens();
    await set(screensRef, screensArrayToObject(screens));
    saveLocalScreens(screens);
    return screens;
  } catch (e) {
    console.warn("Firebase unavailable, using localStorage:", e.message);
    var localScreens = getLocalScreens();
    if (localScreens) return localScreens;
    var screens = buildDefaultScreens();
    saveLocalScreens(screens);
    return screens;
  }
}

export async function getAllScreens() {
  return await initFirebase();
}

export async function getScreen(screenNumber) {
  var num = parseInt(screenNumber, 10);
  var screens = await initFirebase();
  for (var i = 0; i < screens.length; i++) {
    if (screens[i].screen_number === num) {
      return screens[i];
    }
  }
  return null;
}

export async function updateScreen(screenNumber, updates) {
  var num = parseInt(screenNumber, 10);
  var screens = await initFirebase();
  var index = -1;
  for (var i = 0; i < screens.length; i++) {
    if (screens[i].screen_number === num) {
      index = i;
      break;
    }
  }
  if (index === -1) throw new Error("Screen not found");

  var updated = Object.assign({}, screens[index], updates, {
    screen_number: num,
    updated_at: new Date().toISOString()
  });
  screens[index] = updated;
  saveLocalScreens(screens);

  try {
    if (database) await set(ref(database, "screens/screen_" + num), updated);
  } catch (e) {
    console.warn("Firebase write failed:", e.message);
  }
  return updated;
}

export async function bulkUpdateScreens(screenIds, updates) {
  var screens = await initFirebase();
  var safeUpdates = Object.assign({}, updates);
  delete safeUpdates.screen_number;
  delete safeUpdates.custom_name;

  var firebaseUpdates = {};
  var modifiedCount = 0;

  for (var j = 0; j < screenIds.length; j++) {
    var id = parseInt(screenIds[j], 10);
    for (var i = 0; i < screens.length; i++) {
      if (screens[i].screen_number === id) {
        screens[i] = Object.assign({}, screens[i], safeUpdates, {
          updated_at: new Date().toISOString()
        });
        firebaseUpdates["screen_" + id] = screens[i];
        modifiedCount++;
        break;
      }
    }
  }
  saveLocalScreens(screens);

  try {
    if (database) await update(ref(database, "screens"), firebaseUpdates);
  } catch (e) {
    console.warn("Firebase bulk write failed:", e.message);
  }
  return { modified_count: modifiedCount };
}

export async function resetScreen(screenNumber) {
  var num = parseInt(screenNumber, 10);
  var screens = await initFirebase();
  var index = -1;
  for (var i = 0; i < screens.length; i++) {
    if (screens[i].screen_number === num) {
      index = i;
      break;
    }
  }
  if (index === -1) throw new Error("Screen not found");

  var reset = Object.assign({}, DEFAULT_SCREEN_CONFIG, {
    screen_number: num,
    custom_name: "Screen " + num,
    updated_at: new Date().toISOString()
  });
  screens[index] = reset;
  saveLocalScreens(screens);

  try {
    if (database) await set(ref(database, "screens/screen_" + num), reset);
  } catch (e) {
    console.warn("Firebase reset write failed:", e.message);
  }
  return reset;
}

export async function copyScreenConfig(targetScreen, sourceScreen) {
  var screens = await initFirebase();
  var sourceNum = parseInt(sourceScreen, 10);
  var targetNum = parseInt(targetScreen, 10);
  var sourceIndex = -1;
  var targetIndex = -1;
  for (var i = 0; i < screens.length; i++) {
    if (screens[i].screen_number === sourceNum) sourceIndex = i;
    if (screens[i].screen_number === targetNum) targetIndex = i;
  }
  if (sourceIndex === -1 || targetIndex === -1) throw new Error("Screen not found");

  var sourceConfig = Object.assign({}, screens[sourceIndex]);
  delete sourceConfig.screen_number;
  delete sourceConfig.custom_name;

  var updated = Object.assign({}, screens[targetIndex], sourceConfig, {
    updated_at: new Date().toISOString()
  });
  screens[targetIndex] = updated;
  saveLocalScreens(screens);

  try {
    if (database) await set(ref(database, "screens/screen_" + targetNum), updated);
  } catch (e) {
    console.warn("Firebase copy write failed:", e.message);
  }
  return updated;
}

export function onScreenChange(screenNumber, callback) {
  if (!database) return function() {};
  var num = parseInt(screenNumber, 10);
  var screenRef = ref(database, "screens/screen_" + num);
  return onValue(screenRef, function(snapshot) {
    if (snapshot.exists()) {
      callback(snapshot.val());
    }
  });
}

export function onAllScreensChange(callback) {
  if (!database) return function() {};
  var screensRef = ref(database, "screens");
  return onValue(screensRef, function(snapshot) {
    if (snapshot.exists()) {
      var screens = screensObjectToArray(snapshot.val());
      if (screens.length === 6) {
        saveLocalScreens(screens);
        callback(screens);
      }
    }
  });
}

export function isUsingMockData() {
  return !database;
}

export async function triggerRefresh() {
  if (!database) return;
  try {
    await set(ref(database, "refresh_signal"), Date.now());
  } catch (e) {
    console.warn("Firebase refresh signal failed:", e.message);
  }
}

export function onRefreshSignal(callback) {
  if (!database) return function() {};
  var refreshRef = ref(database, "refresh_signal");
  var isFirst = true;
  return onValue(refreshRef, function() {
    if (isFirst) {
      isFirst = false;
      return;
    }
    callback();
  });
}
